import type { Card, GameEvent, Rank, Seat, Team } from "@guandan/engine";
import {
  decodeCompactEvent, projectEvent,
  type ReplayArchiveV2, type WireEvent, type WireEventBody,
} from "@guandan/protocol";

/**
 * Turn a stored archive into the timed event stream the table already renders.
 *
 * A replay tape holds canonical events and, unlike a live table, deliberately
 * exposes every finished hand. The regular table model is still projected for
 * turn/trick rendering; `handsAt` carries the four omniscient hand snapshots
 * used only by the replay surface.
 */

/** Nobody wants to sit through a real thirty-second think. */
const MAX_OFFSET_MS = 1_400;

export interface ReplayStream {
  /**
   * The tape, with `offset` zeroed.
   *
   * Pacing is the viewer's job, not the scheduler's: the scheduler compresses
   * and then skips rendering entirely once it falls far enough behind, which is
   * right for a live table catching up and wrong for a replay, where being
   * "behind" is the whole point. Feeding it one event at a time with no offset
   * keeps its lag at zero, so it animates every event properly and the viewer
   * decides when the next one arrives.
   */
  readonly events: readonly WireEvent[];
  /** How long to wait before feeding each event, in the same order. */
  readonly delays: readonly number[];
  /** Index of the first event of each hand, for jumping between deals. */
  readonly handStarts: readonly number[];
  readonly hands: number;
  /** Four visible hands after each event prefix; index 0 is the empty table. */
  readonly handsAt: readonly ReplayHands[];
}

export type ReplayHands = readonly [
  readonly Card[], readonly Card[], readonly Card[], readonly Card[],
];

const emptyHands = (): [Card[], Card[], Card[], Card[]] => [[], [], [], []];

function updateHands(
  current: [Card[], Card[], Card[], Card[]],
  event: GameEvent,
): [Card[], Card[], Card[], Card[]] {
  if (event.type === "handStarted") return emptyHands();
  if (event.type === "dealt") {
    return ([0, 1, 2, 3] as Seat[]).map((seat) => [
      ...(event.hands[seat] ?? []),
    ]) as [Card[], Card[], Card[], Card[]];
  }
  if (event.type !== "tributePaid" && event.type !== "tributeReturned" && event.type !== "played") {
    return current;
  }

  const next = current.map((hand) => [...hand]) as [Card[], Card[], Card[], Card[]];
  const remove = (seat: Seat, cards: readonly Card[]) => {
    for (const card of cards) {
      const at = next[seat].indexOf(card);
      if (at >= 0) next[seat].splice(at, 1);
    }
  };

  if (event.type === "played") {
    remove(event.seat, event.cards);
  } else {
    remove(event.from, [event.card]);
    next[event.to].push(event.card);
  }
  return next;
}

/**
 * Both teams' levels, tracked across the tape.
 *
 * A `handStarted` event names only the playing team's level; the other team's
 * is whatever it was when they last played. Carrying the pair forward is how
 * the scoreboard stays right for both sides through a whole match.
 */
function trackLevels(levels: [Rank, Rank], event: GameEvent): [Rank, Rank] {
  if (event.type !== "handStarted") return levels;
  const next: [Rank, Rank] = [levels[0], levels[1]];
  next[event.playingTeam as Team] = event.level;
  return next;
}

export function buildReplayStream(archive: ReplayArchiveV2, seat: Seat | null): ReplayStream {
  const events: WireEvent[] = [];
  const delays: number[] = [];
  const handStarts: number[] = [];
  const handsAt: ReplayHands[] = [emptyHands()];
  let levels: [Rank, Rank] = [2 as Rank, 2 as Rank];
  let visibleHands = emptyHands();
  let elapsed = 0;
  let previousHold = 0;

  for (let index = 0; index < archive.events.length; index++) {
    const [seq, delta, code, payload] = archive.events[index] as ReplayArchiveV2["events"][number];
    const event = decodeCompactEvent(code, payload);
    visibleHands = updateHands(visibleHands, event);
    levels = trackLevels(levels, event);
    if (event.type === "handStarted") handStarts.push(events.length);

    const body: WireEventBody | null = projectEvent(event, seat, {
      levels,
      // A replay has no clock running against the viewer and no turn to take,
      // so there is no deadline to render and no token to echo back.
      deadlineAt: null,
      turnReadyAt: elapsed,
      turnToken: "replay",
      ...(event.type === "matchEnded" ? { summary: archive.summary } : {}),
    });
    if (body === null) continue;

    const timing = archive.visualTimings?.[index];
    const wait = Math.min(Math.max(0, timing?.[0] ?? delta), MAX_OFFSET_MS);
    const hold = Math.max(0, timing?.[1] ?? 0);
    delays.push(previousHold + wait);
    elapsed += wait;
    events.push({ seq, t: elapsed, presentAt: elapsed, readyAt: elapsed + hold, offset: wait, hold, body });
    handsAt.push(visibleHands.map((hand) => [...hand]) as [Card[], Card[], Card[], Card[]]);
    elapsed += hold;
    previousHold = hold;
  }

  return { events, delays, handStarts, hands: handStarts.length, handsAt };
}

export type { Card };
